// Facts derived from a war's static setup (MatchSetupView) that the HUD, the renderers and the VFX share: the seats,
// each seat's war capital (MatchSetupView.belligerents, never a scan of the city list) and the instance capacities the
// renderers allocate once per war. A war has any number of seats; nothing here assumes two.

import { CAPTURE, GAME_MODES, presizedBodies, transportCraftCapacity } from '@taxwar/content'
import { InvariantViolation, playerIdsOf, type MapCityView, type MatchSetupView, type PlayerId } from '@taxwar/net'

/** Every seat of the war, ascending (MatchSetupView.belligerents order). */
export function seatsOf(setup: MatchSetupView): readonly PlayerId[] {
  return playerIdsOf(setup.belligerents.length)
}

/** A seat's war capital (never captured, GDD §5: the war-start capital is the capital for the whole war). */
export function capitalOf(setup: MatchSetupView, seat: PlayerId): MapCityView {
  const belligerent = setup.belligerents[seat]
  if (belligerent === undefined) throw new InvariantViolation(`seat ${seat} is not a belligerent of this war`)
  const city = setup.cities[belligerent.capital]
  if (city === undefined || city.index !== belligerent.capital) throw new InvariantViolation(`seat ${seat}'s capital ${belligerent.capital} is not on the war map`)
  if (!city.capital) throw new InvariantViolation(`seat ${seat}'s capital ${city.name} is not flagged as a capital`)
  return city
}

/** Instances of each kind renderers allocate at war start; every batch grows past its size when a war outgrows it. */
export interface WarCapacity {
  /** Unit entities: every one has at least one body, so the seats' presized bodies (content presizedBodies). */
  readonly units: number
  /** Buildings: every city at the capital's slot count (an upper bound, content CAPTURE). */
  readonly buildings: number
  /** City guns: one per city. */
  readonly cityGuns: number
  /** Transport hulls: every building slot (capital count) can hold a berth, each with a full vessel complement. */
  readonly transports: number
}

export function warCapacity(setup: MatchSetupView): WarCapacity {
  return {
    units: presizedBodies(GAME_MODES[setup.mode].bodyCap) * setup.belligerents.length,
    buildings: setup.cities.length * CAPTURE.capitalSlots,
    cityGuns: setup.cities.length,
    transports: transportCraftCapacity(setup.cities.length),
  }
}
